import { handleActions } from 'redux-actions';
import cloneDeep from 'lodash/cloneDeep';
import values from 'lodash/values';
import find from 'lodash/find';
import moment from 'moment';

import http from '../shared/http';
import endpoints from '../../config/endpoints';

import intl from '../shared/intl';
import { default as createCalendar, getDateRangeFromCalendar } from '../shared/calendar';
import checkPermissions from '../shared/check-permissions';
import { default as createConfigForm } from './components/config-form';
import { default as createTransferForm } from './components/transfer-form';
import { default as createProtectionForm } from './components/protection-form';

const configForm = createConfigForm({
	namespace: 'wm',
	stateNodeName: 'configForm',
	formName: 'configForm'
});

const transferForm = createTransferForm({
	namespace: 'wm',
	stateNodeName: 'transferForm',
	formName: 'transferForm'
});

const protectionForm = createProtectionForm({
	namespace: 'wm',
	stateNodeName: 'protectionForm',
	formName: 'protectionForm'
});

const calendar = createCalendar({
	namespace: 'wm',
	stateNodeName: 'calendar',
	defaultValue: {
		start: moment().subtract(1, 'week').format('YYYY-MM-DD'),
		end: moment().format('YYYY-MM-DD')
	}
});

// actions types
const FETCH_CONFIG_SUCCESS = 'wm/fetch-config-success';
const FETCH_PURSES_SUCCESS = 'wm/fetch-purses-success';
const FETCH_HISTORY_SUCCESS = 'wm/fetch-history-success';
const UPDATE_PURSE = 'wm/update-purse';

const FETCH_DATA_ERROR = 'wm/fetch-data-error';

const TOGGLE_PROGRESS_BAR = 'wm/toggle-progress-bar';

// permissions
export const allowedPermissions = {
	view: 'wm'
};

// initial state
const initialState = {
	data: null,
	calendar: calendar.initialState,
	table: {
		columns: {
			datecrt: {
				index: 'datecrt',
				title: 'Date'
			},
			pursesrc: {
				index: 'pursesrc',
				title: 'Purse from'
			},
			pursedest: {
				index: 'pursedest',
				title: 'Purse to'
			},
			amount: {
				index: 'amount',
				title: 'Amount (comiss.)'
			},
			desc: {
				index: 'desc',
				title: 'Description'
			},
			actions: {
				title: 'Actions',
				className: 'actions'
			}
		}
	},
	configForm: configForm.initialState,
	transferForm: transferForm.initialState,
	protectionForm: protectionForm.initialState,
	showProgressBar: false,
	error: false
};

// actions
export const actions = Object.assign({
	fetchData: (context, purse) => (dispatch, getState) => {
		return http({
			url: `${endpoints.WM.CONFIG}`,
			method: 'get',
			actions: [FETCH_CONFIG_SUCCESS, FETCH_DATA_ERROR]
		})(dispatch)
			.then(action => {
				if (action.type === FETCH_CONFIG_SUCCESS && action.payload.data) {
					return http({
						url: `${endpoints.WM.PURSES}`,
						method: 'get',
						actions: [FETCH_PURSES_SUCCESS, FETCH_DATA_ERROR],
						addToResponse: { purse }
					})(dispatch)
						.then(action => {
							if (action.type === FETCH_PURSES_SUCCESS && action.payload.data.length) {
								dispatch(actions.fetchHistory());
							}
						});
				}
			});
	},

	toggleProgressBar: () => ({
		type: TOGGLE_PROGRESS_BAR
	}),

	updatePurse: purse => (dispatch, getState) => {
		dispatch({
			type: UPDATE_PURSE,
			payload: {
				data: purse
			}
		});
		dispatch(actions.toggleProgressBar());
		dispatch(actions.fetchHistory());
	},

	fetchHistory: () => (dispatch, getState) => {
		const { calendar, data: { purse: { pursename } } } = getState().wm;
		const range = getDateRangeFromCalendar(calendar);
		const datestart = moment(range.start).format('YYYYMMDD HH:mm:ss');
		const datefinish = moment(range.end).format('YYYYMMDD HH:mm:ss');
		return http({
			url: `${endpoints.WM.HISTORY}?purse=${pursename}&datestart=${datestart}&datefinish=${datefinish}`,
			method: 'get',
			actions: [FETCH_HISTORY_SUCCESS, FETCH_DATA_ERROR]
		})(dispatch);
	}
}, configForm.actions, transferForm.actions, protectionForm.actions, calendar.actions);

// reducers
const reducers = Object.assign({
	[FETCH_CONFIG_SUCCESS]: (state, action) => ({
		...state,
		data: {
			wm_config: action.payload.data || undefined
		},
		showProgressBar: !!action.payload.data,
		error: false
	}),
	[FETCH_PURSES_SUCCESS]: (state, action) => {
		let predefinedPurse = action.meta.purse;
		let purse = null;
		if (action.payload.data.length) {
			purse = predefinedPurse ? find(action.payload.data, {pursename: predefinedPurse}) : action.payload.data[0];
		}
		return ({
			...state,
			data: {
				...state.data,
				purses: action.payload.data || [],
				purse
			},
			showProgressBar: true,
			error: false
		});
	},
	[FETCH_HISTORY_SUCCESS]: (state, action) => ({
		...state,
		data: {
			...state.data,
			history: action.payload.data || []
		},
		showProgressBar: false,
		error: false
	}),
	[UPDATE_PURSE]: (state, action) => ({
		...state,
		data: {
			...state.data,
			purse: find(state.data.purses, {pursename: action.payload.data}) || null
		}
	}),
	[FETCH_DATA_ERROR]: (state, action) => ({ ...state, error: true, showProgressBar: false }),
	[TOGGLE_PROGRESS_BAR]: state => ({ ...state, showProgressBar: !state.showProgressBar })
}, configForm.reducers, transferForm.reducers, protectionForm.reducers, calendar.reducers);

export default handleActions(reducers, cloneDeep(initialState));

// selectors
export const selectors = {
	getData: state => state.wm.data,
	getConfig: state => state.wm.data && state.wm.data.wm_config,
	getPurse: state => state.wm.data && state.wm.data.purse,
	getPurses: state => state.wm.data && state.wm.data.purses &&
		state.wm.data.purses.map(purse => ({label: `${purse.pursename} (${purse.amount})`, value: purse.pursename})),
	getTable: state => {
		const { table, data } = state.wm;
		return {
			headers: values(table.columns),
			rows: data && data.history || []
		};
	},
	getCalendar: state => calendar.selectors.getCalendar(state => state.wm.calendar, state => state)(state),
	getIntl: state => intl(state),
	getProgressBar: state => state.wm.showProgressBar,
	getCan: state => checkPermissions(state, allowedPermissions),
	getError: state => state.wm.error,
	getShouldShowConfigForm: configForm.selectors.getShouldShowConfigForm,
	getTransferFormInitialValues: transferForm.selectors.getInitialValues,
	getShouldShowTransferForm: transferForm.selectors.getShouldShowTransferForm,
	getShouldShowProtectionForm: protectionForm.selectors.getShouldShowProtectionForm,
	getProtectionFormInitialValues: protectionForm.selectors.getInitialValues
};



// WEBPACK FOOTER //
// ./frontend/admin-dashboard/src/modules/wm/index.js